<ng-template #teamTaskActions>
  @if (accountKind.isTeam() && task()?.id && task()?.accountId === workspaceIdentity()?.spaceTenantId) {
    <div class="flex flex-wrap items-center gap-4 px-8 py-4" role="group" aria-label="Task actions">
      <app-handover-assign-button
        [label]="handover.buttonLabel()"
        [ariaLabel]="handover.accessibleName()"
        [disabled]="!handover.mayAssign()"
        [describedBy]="handover.mayAssign() ? null : handover.assignReasonId"
        [attr.title]="handover.mayAssign() ? null : handover.assignReason"
        (click)="handover.openAssign()"
      />
      @if (!handover.mayAssign()) {
        <span class="sr-only" [id]="handover.assignReasonId">{{ handover.assignReason }}</span>
      }
      <button
        type="button"
        class="min-h-16 max-w-full rounded border border-border px-8 py-2 text-base leading-10 [overflow-wrap:anywhere]"
        (click)="openWaiting()"
      >
        Waiting on someone…
      </button>
    </div>
  }
</ng-template>

@if (insideModal()) {
  <ng-container [ngTemplateOutlet]="teamTaskActions" />
}

<div class="skin-detail-heading flex flex-row items-center gap-4" (resize)="handleResize($event)">
  @if (canGoBack()) {
    <app-button2 appearance="secondary-ghost" size="s" aria-label="Go back" (click)="handleGoBack()">
      <app-icon name="chevron-left" />
    </app-button2>
  }

  <!-- stitch-app-shell TaskCheck: the 20px circle sits in a 24px box beside the title. -->
  <app-circular-progress
    class="mx-1"
    *ngIf="task().progress >= 0"
    [progress]="task().progress"
    [size]="20"
    [priority]="form.controls.priority.value"
    [completed]="task().completed"
    (onClick)="handleComplete()"
  />

  <app-input
    class="flex-1 min-w-[96px]"
    appearance="sapphire-inline"
    [inputFormControl]="form.controls.title"
    placeholder="New Task"
    [errorPlaceholder]="'Add title'"
    [withErrors]="submitted"
    size="s"
    [customStyles]="contracted ? { 'text-overflow': 'ellipsis' } : undefined"
    [attr.title]="contracted ? form.controls.title.value : null"
  />

  <div class="flex flex-row items-center" [class.gap-2]="contracted" [class.gap-4]="!contracted">
    <div [appPreventDirtyClick]="dirtyFormRef" class="contents">
      <!-- [MUST-002] 'Bug' debug quick-action hidden per task; uncomment to re-enable
      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="bug"
        [active]="selectedTab === 'debug'"
        matTooltip="Debug"
        (onClick)="selectedTab = 'debug'"
      />
      -->

      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="sapphire-archive2"
        [active]="form.controls.archived.value"
        [matTooltip]="form.controls.archived.value ? Labels.RestoreFromArchive : Labels.Archive"
        [disabled]="!task()?.id"
        (onClick)="archive()"
      />

      <stch-trash-action
        *ngIf="!contracted"
        [deleted]="form.controls.deleted.value"
        [disabled]="!task()?.id"
        (onClick)="delete()"
      />

      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="sapphire-share"
        matTooltip="Share"
        [disabled]="!task()?.id"
        [stchShareContextMenu]="task()"
        [stchShareContextMenuDisabled]="!task()?.id"
      />

      <ng-container *ngComponentOutlet="workspaceComponents['app-item-workspace-chip']; inputs: workspaceChipInputs" />

      <app-button
        appearance="sapphire-secondary-ghost"
        size="xs"
        icon="sapphire-ellipsis"
        matTooltip="More options"
        ariaLabel="More options"
        [disabled]="!task()?.id"
        (appTaskContextMenuTab)="$event === 'access' ? openWorkspaceAccess() : $event === 'versions' ? openVersionHistory() : openItemComments()"
        [appTaskContextMenuElementLocation]="UILocations.mainView"
        [appTaskContextMenu]="task()"
        [appTaskContextMenuDisabled]="!task()?.id"
        appTaskContextMenuMode="popover"
        [printEnabled]="true"
      />
    </div>

    @if (showCloseButton) {
      <app-button appearance="sapphire-secondary-ghost" size="xs" icon="sapphire-close" (click)="handleClose()" />
    }
  </div>
</div>

@if (!task().id && workspaceIdentity(); as identity) {
  <app-tenancy-selector
    [spaceTitle]="identity.spaceTitle"
    [spaceTenantId]="identity.spaceTenantId"
    [memberTenantId]="identity.memberTenantId"
    itemNoun="task"
    [locked]="form.controls.uploads.value.length > 0 || !!task().accountId"
    [lockedReason]="task().accountId ? (task().projectId ? 'Workspace is set by this project in ' + (sharingTenantId() === identity.spaceTenantId ? identity.spaceTitle : 'My Workspace') + '.' : 'Workspace is set by the message this task was made from.') : null"
    [(tenantId)]="sharingTenantId"
  />
}

<!-- Badge shares the timestamps line, right-aligned, so the header shows where the item lives.
     Only `pr-8` here: app-stitch-timestamps brings its own `px-8`, and this matches the right padding
     the title row above uses, so the badge lines up under the header icons. It hangs 2px under them
     (`self-start -mt-[2px]`), as the prototype's badge column does, rather than centring on the dates. -->
<div class="flex items-center justify-between gap-2 pr-8">
  <!-- Start | Due under the title, opening the Details tab's date & time picker (back by Johanna's call,
       2026-10-21); Created · Updated lives at the bottom of Details. -->
  <div class="task-header-meta" [class.no-check]="!(task().progress >= 0)">
    <stch-header-dates
      [fromDate]="form.controls.fromDate"
      [fromTime]="form.controls.fromTime"
      [toDate]="form.controls.toDate"
      [toTime]="form.controls.toTime"
      [allDay]="form.controls.allDay.value"
      [overdue]="isOverdue()"
      [wallClockZone]="form.controls.timeZone.value"
    />
  </div>
  <ng-container *ngComponentOutlet="workspaceComponents['app-item-team-label']; inputs: workspaceLabelInputs" />
  <!-- stitch-app-shell FolderBadge (Johanna, 2026-10-20): a task in a project always shows the project. -->
  @if (headerProject(); as project) {
    <app-container-badge class="min-w-0 self-start -mt-[2px]" type="project" [title]="project.title" [color]="project.color" />
  } @else if (locationBadge(); as badge) {
    <app-search-location-badge class="min-w-0 self-start -mt-[2px]" [type]="badge.type" [name]="badge.name ?? ''" />
  }
</div>

@if (accountKind.isTeam() && waitingDetails(); as waiting) {
  <div class="px-8 py-4">
    <p>Waiting for: {{ waiting.reason }}</p>
    <p>{{ waiting.name }} · {{ waiting.since | dateFormat: 'dateTime' }}</p>
    <button type="button" class="rounded border border-border px-8 py-4" (click)="stopWaiting()">Stop waiting</button>
  </div>
}
@if (accountKind.isTeam() && waitingActionError()) { <p role="alert">{{ waitingActionError() }}</p> }

@if (!insideModal()) {
  <ng-container [ngTemplateOutlet]="teamTaskActions" />
}

<div class="skin-detail-tabs flex flex-row items-center h-[42px] gap-3 px-4 border-b border-gray-200">
  <app-tabs appearance="sapphire" [values]="workspaceTabs" [(value)]="selectedTab" size="m" />

  <div class="flex-1"></div>

  <stch-knowledge-button [active]="displayKnowledge" (click)="showKnowledge()" />
</div>

<app-task-handover
  #handover
  [insideModal]="insideModal()"
  (closed)="assignmentTarget.set(null)"
  [enabled]="
    accountKind.isTeam() &&
    !!handoverTask()?.id &&
    !!workspaceIdentity()?.spaceTenantId &&
    handoverTask()?.accountId === workspaceIdentity()?.spaceTenantId
  "
  [taskId]="handoverTask()?.id"
  [tenantId]="handoverTask()?.accountId"
  [responsibleId]="handoverTask()?.assigneeId"
/>
<app-item-review
  [api]="reviewApi"
  [enabled]="accountKind.isTeam() && !!reviewTenantId()"
  [itemId]="reviewItemId()"
  [tenantId]="reviewTenantId()"
  [insideModal]="insideModal()"
  [stitchType]="StitchType.TASK"
  [contentVersion]="reviewContentVersion()"
  (closed)="reviewTarget.set(null)"
/>

@if (accountKind.isTeam() && insideModal() && waitingTarget()?.parentId === task().id) {
  @if (waitingTarget(); as target) {
    <app-task-waiting [task]="target.task" [insideModal]="true" (saved)="waitingSaved($event)" (closed)="waitingTarget.set(null)" />
  }
}

@if (insideModal() && dispositionTarget()?.parentId === task().id) {
  @if (dispositionTarget(); as target) {
    <app-task-disposition [target]="target.target" [insideModal]="true" (closed)="dispositionTarget.set(null)" />
  }
}

<div
  class="form"
  appDroppable
  appDroppableHoverClass="drag-over"
  [appDroppableDisabled]="!stitchItem?.id"
  [appDroppablePredicate]="dndPredicate"
  (appDroppableDrop)="dndDrop($event)"
>
  <div class="drop-area-border"></div>

  @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
    <app-entity-hub-search [contactId]="task()?.id" />
  }

  <div class="form-heading">
    <div class="form-heading-nav">
      <div class="form-heading-nav-separator"></div>

    </div>

    <div *ngIf="displayKnowledge" class="form-heading-nav-underline"></div>

    <!-- One Knowledge box, rows in the skin's order: Knots · Tags · Connections. On the Stitch tab the rows are
         the knowledge hub's facets (#2481), read-only, and hidden without hub access. -->
    <div
      class="form-knowledge"
      [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
    >
      <app-knots
        class="form-knots"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="task() | toArray"
        [control]="form.controls.knots"
        [stateKey]="StateKey.knotsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="task()?.id"
      />

      <app-tags
        class="form-tags"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="[task()]"
        [control]="form.controls.tags"
        [stateKey]="StateKey.tagsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="task()?.id"
      />

      <app-connections
        class="form-knots"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="[task()]"
        [control]="form.controls.connections"
        [stateKey]="StateKey.connectionsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="task()?.id"
      />
    </div>

    <div class="form-heading-nav-underline"></div>
  </div>

  <app-scroll
    *ngIf="(selectedTab !== 'stitch' || (permissionsLoaded() && !hasKnowledgeHubAccess()))"
    [ngSwitch]="selectedTab"
    class="form-scroll"
    (top)="displayScrollShadow = !$event"
  >
    <div *ngIf="displayScrollShadow" class="form-scroll-shadow"></div>

    <app-task-form-subtasks
      *ngSwitchCase="'task'"
      [task]="task()"
      [filters]="tasksFilters"
      [triggerRefresh]="triggerRefreshSubtasks"
      [requestContext]="itemTenantContext()"
      [(pendingSubtasks)]="pendingSubtasks"
    />

    <!--
      stitch-app-shell FormKit (TaskForm): three sections split by Gray/200 rules, every field named above
      it. When: Start, Due and Repeats on one row, All day and the time zone. Reminders. Then Priority and
      Project (with its Section and Column), the description and when the task was made.
    -->
    <div *ngSwitchCase="'details'" class="form-kit" [class.contracted]="contracted">
      <section class="form-kit-section">
        <!-- Start date · Due date · Repeats on one row (FormKit Row), wrapping when the panel is narrow. -->
        <div class="form-kit-row">
          <app-date-range-picker
            class="form-kit-field-range"
            startLabel="Start date"
            endLabel="Due date"
            separator="none"
            [fromDate]="form.controls.fromDate"
            [toDate]="form.controls.toDate"
            [fromTime]="form.controls.fromTime"
            [toTime]="form.controls.toTime"
            [allDay]="form.controls.allDay.value"
            [overdue]="isOverdue()"
          />

          <div class="form-kit-field">
            <span class="form-kit-label">Repeats</span>
            <app-recurrence-picker
              appearance="sapphire-outline"
              [control]="form.controls.recurrence"
              [referenceDate]="form.controls.fromDate"
              size="m"
            />
          </div>
        </div>

        <div class="form-kit-inline">
          <app-checkbox2 [formControl]="form.controls.allDay" label="All day" size="m" />

          <app-timezone-input
            [control]="form.controls.timeZone"
            placeholder="Select Time Zone"
            appearance="sapphire"
            buttonSize="m"
            leadingIcon="sapphire-globe"
            [caption]="tzSeeder.caption()"
          />
        </div>
      </section>

      <section class="form-kit-section form-kit-section-compact">
        <h3 class="form-kit-section-title">Reminders</h3>
        <stch-reminders-picker anchors="task" [control]="form.controls.reminders" />
      </section>

      <section class="form-kit-section">
        <div class="form-kit-row">
          <div class="form-kit-field">
            <span class="form-kit-label">Priority</span>
            <app-dropdown-input
              size="m"
              [inputFormControl]="form.controls.priority"
              [options]="priorityOptions"
              [appearance]="'sapphire-outline'"
              [icon]="selectedPriorityIcon()"
            />
          </div>

          <div class="form-kit-field">
            <span class="form-kit-label">Project</span>
            <app-autocomplete-input
              appearance="sapphire-outline"
              placeholder="Add To Project"
              [suggestions]="suggestions"
              [inputFormControl]="form.controls.projectId"
              size="s"
              [withErrors]="submitted"
              [withClear]="true"
            />
          </div>
        </div>

        <div class="form-kit-row" *ngIf="form.controls.projectId.value">
          <div class="form-kit-field">
            <span class="form-kit-label">Section</span>
            <app-autocomplete-input
              appearance="sapphire-outline"
              placeholder="Section"
              [suggestions]="sectionsSuggestions"
              [inputFormControl]="form.controls.sectionId"
              size="s"
              [withErrors]="submitted"
              [withClear]="true"
            />
          </div>

          <div class="form-kit-field">
            <span class="form-kit-label">Column</span>
            <app-autocomplete-input
              appearance="sapphire-outline"
              placeholder="Column"
              [suggestions]="columnsSuggestions"
              [inputFormControl]="form.controls.columnId"
              size="s"
              [withErrors]="submitted"
              [withClear]="true"
            />
          </div>
        </div>

        <stch-item-description-field
          [control]="form.controls.description"
          [item]="task()"
          [tagsControl]="form.controls['tags']"
          [knotsControl]="form.controls['knots']"
        />

        <!-- When it was made lives here, as on events and projects; the header shows Start | Due. -->
        @if (task()?.createdAt) {
          <app-stitch-timestamps class="form-kit-timestamps mt-4 block" [createdAt]="task()?.createdAt" [updatedAt]="task()?.updatedAt" />
        }
      </section>

      <!-- Team items (item handover): who is responsible and who made the task, as a FormKit InfoList. -->
      @if (handover.enabled()) {
        <section class="form-kit-section">
          <dl class="form-kit-info">
            <div class="form-kit-info-row">
              <dt>Responsible</dt>
              <dd data-testid="details-responsible">{{ handover.responsibleName() }}</dd>
            </div>
            @if (handover.creatorName(); as author) {
              <div class="form-kit-info-row">
                <dt>Created by</dt>
                <dd data-testid="details-authored-by">{{ author }}</dd>
              </div>
            }
          </dl>
        </section>
      }
    </div>

    <ng-container *ngSwitchCase="'attachments'">
      @if (audienceResolved()) {
        <app-attachments-list
          class="form-attachments"
          [stitchItem]="stitchItem"
          [tenantItems]="uploadTenantItems()"
          [inputControl]="form.controls.uploads"
          [contextMenuStateVariant]="attachmentsMenuStateVariant"
          (listStateChange)="attachmentsListState = $event"
          (onClickItem)="handleClickItem($event)"
          (onDblClickItem)="handleDblClickItem($event)"
          (uploading)="onUploadingChange($event)"
        />
      }
    </ng-container>

    <app-context-menu-state-indicator
      *ngSwitchCase="'stitch'"
      class="block ml-auto w-fit mt-5 mr-4"
      svgIcon="filters"
      appStitchListContextMenu
      (appStitchListContextMenuChange)="stitchListState = $event"
    />

    <app-linked-info-list
      *ngSwitchCase="'stitch'"
      class="form-linked-info"
      [stitch]="task()"
      [state]="stitchListState"
      (clickStitchItem)="handleClickItem($event)"
      (dblClickStitchItem)="handleDblClickItem($event)"
    />

    <ng-container *ngSwitchCase="'access'">
      <ng-container *ngComponentOutlet="workspaceComponents['app-item-workspace-access']; inputs: workspaceLabelInputs" />
    </ng-container>

    <stch-comments (countChanged)="commentsCount.set($event)" *ngSwitchCase="'comments'" class="form-linked-info" [stitch]="task()" [insideTeam]="insideTeam" />

    <ng-container *ngSwitchCase="'activity'">
      @if (itemChangesInputs; as inputs) {
        <ng-container *ngComponentOutlet="workspaceComponents['app-item-changes']; inputs: inputs" />
      }
    </ng-container>

    <ng-container *ngSwitchCase="'versions'">
      @if (historyAvailable()) {
        <ng-container *ngComponentOutlet="workspaceComponents['app-task-version-history']; inputs: versionHistoryInputs" />
      }
    </ng-container>

    <stch-json-viewer *ngSwitchCase="'debug'" [json]="task()?.debug" />
  </app-scroll>

  @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
    <app-stitch-hub
      [stitch]="task()"
      (clickStitchItem)="handleClickItem($event)"
      (dblClickStitchItem)="handleDblClickItem($event)"
    />
  }

  <stch-save-cancel-panel
    [enabled]="!pristine"
    [uploading]="isUploading()"
    [saveLabel]="saveLabel()"
    (onCancel)="handleCancel()"
    (onSave)="handleSubmit()"
  />

</div>

<ng-container *ngComponentOutlet="workspaceComponents['app-item-quick-comment']; inputs: quickCommentInputs" />

<!-- stitch-app-shell breadcrumbs footer (Johanna, 2026-10-20): in the main view the task's place reads as a path,
     with the workspace first in a team; the side panel and modals keep the workspace footer. -->
@if (navigation.enabled && task().id) {
  <app-detail-breadcrumbs
    [crumbs]="breadcrumbs()"
    [status]="form.controls.archived.value ? 'Archived' : form.controls.deleted.value ? 'Trash' : undefined"
  />
} @else {
  <ng-container *ngComponentOutlet="workspaceComponents['app-item-workspace-footer']; inputs: workspaceFooterInputs" />
}
